import {makeAutoObservable, reaction} from 'mobx';

import type {ActiveChannelStore} from '../../../../channel/stores/ActiveChannel/ActiveChannel.store';
import type {PostModel} from '../../../../post/models/Post/Post.model';

import {emitter} from '../../../../../../utils/eventEmitter';
import {
  type Destructible,
  ReactionDisposeManager,
  type WithReactionDispose,
} from '../../../../../../utils/store';
import {PostType} from '../../../../post/models/Post/utils';

export class VoicePlaybackController
  implements Destructible, WithReactionDispose
{
  static PAUSE_EVENT_KEY = 'voice-playback-controller-pause-event';
  static PLAY_EVENT_KEY = 'voice-playback-controller-play-event';
  readonly disposer = new ReactionDisposeManager();
  private currentVoiceMessageId = '';
  private isInitialized = false;
  // voiceMessageId -> nextVoiceMessageId
  private nextIds = new Map<string, string>();
  private playNextTimeout = 0;

  constructor(private parent: ActiveChannelStore) {
    makeAutoObservable(this, undefined, {
      autoBind: true,
    });
  }

  init() {
    if (this.isInitialized) {
      return;
    }

    this.isInitialized = true;
    this.setup();

    this.setupReactions();
  }

  destruct() {
    this.setCurrentVoiceMessageId('');

    emitter.off(
      VoicePlaybackController.CURRENT_VOICE_MESSAGE_ID_EVENT_KEY,
      this.onCurrentVoiceMessageIdChange,
    );
    emitter.off(VoicePlaybackController.PLAY_NEXT_EVENT_KEY, this.onPlayNext);

    this.disposer.dispose();

    clearTimeout(this.playNextTimeout);

    this.isInitialized = false;
    this.nextIds.clear();
  }

  static emitCurrentVoiceMessageId(id: string) {
    emitter.emit(
      VoicePlaybackController.CURRENT_VOICE_MESSAGE_ID_EVENT_KEY,
      id,
    );
  }

  static emitPause(id: string) {
    emitter.emit(VoicePlaybackController.PAUSE_EVENT_KEY, id);
  }

  static emitPlay(id: string) {
    emitter.emit(VoicePlaybackController.PLAY_EVENT_KEY, id);
  }

  static emitPlayNext(id: string) {
    emitter.emit(VoicePlaybackController.PLAY_NEXT_EVENT_KEY, id);
  }

  collectVoiceMessageIds(posts: PostModel[]) {
    posts.forEach((post, i, itself) => {
      const isCurrentPostVoice = post.postType === PostType.VOICE;
      const nextPost = itself[i + 1];

      const isNextPostVoice = nextPost?.postType === PostType.VOICE;

      if (isCurrentPostVoice && isNextPostVoice) {
        const currentVoiceMessage = post.renderAttachments[0]!;
        const nextVoiceMessage = nextPost.renderAttachments[0]!;

        this.nextIds.set(currentVoiceMessage.id, nextVoiceMessage.id);
      }
    });
  }

  reset() {
    this.setCurrentVoiceMessageId('');

    this.nextIds.clear();

    return this;
  }

  private onCurrentVoiceMessageIdChange(id: string) {
    const prevId = this.currentVoiceMessageId;

    this.setCurrentVoiceMessageId(id);

    if (prevId && prevId !== id) {
      VoicePlaybackController.emitPause(prevId);
    }
  }

  private onPlayNext() {
    const nextId = this.nextIds.get(this.currentVoiceMessageId);

    if (nextId) {
      this.playNextTimeout = +setTimeout(() => {
        VoicePlaybackController.emitPlay(nextId);
      }, 300);
    }
  }

  private setCurrentVoiceMessageId(id: string) {
    this.currentVoiceMessageId = id;
  }

  private setup() {
    emitter.on(
      VoicePlaybackController.CURRENT_VOICE_MESSAGE_ID_EVENT_KEY,
      this.onCurrentVoiceMessageIdChange,
    );
    emitter.on(VoicePlaybackController.PLAY_NEXT_EVENT_KEY, this.onPlayNext);
  }

  private setupReactions() {
    this.disposer.watch(
      'channel_change',
      reaction(
        () => this.parent.channel,
        (activeChannel, prevChannel) => {
          if (prevChannel?.id && activeChannel?.id) {
            VoicePlaybackController.emitPause(this.currentVoiceMessageId);
            clearTimeout(this.playNextTimeout);
            this.setCurrentVoiceMessageId('');
            this.nextIds.clear();
          }
        },
      ),
    );
  }

  private static CURRENT_VOICE_MESSAGE_ID_EVENT_KEY =
    'voice-playback-controller-current-voice-message-id-event';
  private static PLAY_NEXT_EVENT_KEY =
    'voice-playback-controller-play-next-event';
}
